/**
 * DIEUKYDIEU.TV - SITEMAP & ARTICLES API SERVICE
 * Connects UI to backend categories & articles endpoints with mock fallback.
 * Under 300 lines rule compliant.
 */

import { API_CONFIG } from './config.js';
import { apiClient } from './client.js';
import { mockAdapter } from './mock/mock-adapter.js';

export const sitemapApi = {
    /**
     * Get hierarchy category tree
     */
    async getCategoriesTree() {
        if (API_CONFIG.USE_MOCK) {
            return (await mockAdapter.getCategoriesTree()).data;
        }

        try {
            const res = await apiClient.get('/categories/tree');
            return res.data || res;
        } catch (err) {
            console.warn('[sitemapApi] Failed fetching categories tree, falling back to mock:', err.message);
            if (API_CONFIG.ENABLE_FALLBACK_ON_ERROR) {
                return (await mockAdapter.getCategoriesTree()).data;
            }
            throw err;
        }
    },

    /**
     * Get all categories with sample articles
     */
    async getAllCategories() {
        if (API_CONFIG.USE_MOCK) {
            return (await mockAdapter.getAllCategories()).data;
        }

        try {
            const res = await apiClient.get('/categories');
            return res.data || res;
        } catch (err) {
            console.warn('[sitemapApi] Failed fetching categories, falling back to mock:', err.message);
            if (API_CONFIG.ENABLE_FALLBACK_ON_ERROR) {
                return (await mockAdapter.getAllCategories()).data;
            }
            throw err;
        }
    },

    /**
     * Get single category by slug or id
     */
    async getCategoryBySlug(slug) {
        if (API_CONFIG.USE_MOCK) {
            return (await mockAdapter.getCategoryBySlug(slug)).data;
        }

        try {
            const res = await apiClient.get(`/categories/${encodeURIComponent(slug)}`);
            return res.data || res;
        } catch (err) {
            console.warn(`[sitemapApi] Failed fetching category ${slug}, falling back to mock:`, err.message);
            if (API_CONFIG.ENABLE_FALLBACK_ON_ERROR) {
                return (await mockAdapter.getCategoryBySlug(slug)).data;
            }
            throw err;
        }
    },

    /**
     * Get article details by ID or Slug
     */
    async getArticleById(idOrSlug) {
        if (API_CONFIG.USE_MOCK) {
            return (await mockAdapter.getArticleById(idOrSlug)).data;
        }

        try {
            const res = await apiClient.get(`/articles/${encodeURIComponent(idOrSlug)}`);
            return res.data || res;
        } catch (err) {
            console.warn(`[sitemapApi] Failed fetching article ${idOrSlug}, falling back to mock:`, err.message);
            if (API_CONFIG.ENABLE_FALLBACK_ON_ERROR) {
                return (await mockAdapter.getArticleById(idOrSlug)).data;
            }
            throw err;
        }
    },

    /**
     * Get paginated articles for a category
     */
    async getArticlesByCategory(categorySlug, page = 1, limit = 12) {
        if (API_CONFIG.USE_MOCK) {
            return (await mockAdapter.getArticlesByCategory(categorySlug, page, limit)).data;
        }

        try {
            const res = await apiClient.get('/articles', {
                category: categorySlug,
                page,
                limit
            });
            return res.data || res;
        } catch (err) {
            console.warn(`[sitemapApi] Failed fetching articles for ${categorySlug}, falling back to mock:`, err.message);
            if (API_CONFIG.ENABLE_FALLBACK_ON_ERROR) {
                return (await mockAdapter.getArticlesByCategory(categorySlug, page, limit)).data;
            }
            throw err;
        }
    },

    /**
     * Like / React to an article
     */
    async toggleArticleLike(articleId, isLiked) {
        if (API_CONFIG.USE_MOCK) {
            return { success: true, articleId, isLiked };
        }

        try {
            return await apiClient.post(`/articles/${articleId}/like`, { isLiked });
        } catch (err) {
            console.warn('[sitemapApi] Like toggle failed on server:', err.message);
            return { success: false, error: err.message };
        }
    }
};
